<template>
  <div class="vz-measure-input" :class="measureClass">
    <vz-input
      ref="inputControl"
      v-model="inputValue"
      class="vz-measure-input-control"
      error-type="message"
      :required="required"
      :label="label"
      :small="small"
      :big="big"
      :disabled="disabled"
      :max-length="maxLength"
      :rules="rules"
      @blur="onBlur"
      @change="onChange"
      @focus="onFocus"
      @update:modelValue="onInput"
      @click="onClick"
      @dblclick="onDblClick"
      @keydown="onKeydown"
      @keyup="onKeyup"
      @keypress="onKeypress"
      @mousedown="onMousedown"
      @mousemove="onMousemove"
      @mouseout="onMouseout"
      @mouseover="onMouseover"
      @mouseup="onMouseup"
      @mousewheel="onMousewheel"
      @clear="clear"
    >
      <template #label>
        <slot name="label" />
      </template>
    </vz-input>
    <div class="vz-measure-input-measure">
      <vz-tooltip-info
        v-if="hasInfoForBY"
        text="Указание страховой суммы возможно только в рублях РФ"
      >
        <template #custom-icon>
          <vz-icon
            :size="16"
            url="/svg/vz-russia-flag.svg"
          />
        </template>
      </vz-tooltip-info>
      <span v-html="measure" />
    </div>

    <vz-invalid-message v-if="displayMaxNotice" :block="errorBlockStyle" class="vz-measure-input-max-notice">
      <span v-html="maxNoticeText" />
    </vz-invalid-message>
  </div>
</template>

<script setup lang="ts">
import CreateNumberMask from 'text-mask-addons/dist/createNumberMask.js'
import { VueMaskFilter } from 'v-mask'
import { ComponentPublicInstance, PropType } from 'vue'
import VzInput from './vz-input.vue';
import {ValidationRule} from '~/types/types';
import VzTooltipInfo from "~/components/ui-kit/vz-tooltip-info.vue";

const emit = defineEmits([
  'update:modelValue',
  'blur',
  'focus',
  'click',
  'change',
  'dblclick',
  'mousedown',
  'mousemove',
  'mouseout',
  'mouseover',
  'keydown',
  'mouseup',
  'mousewheel',
  'keyup',
  'keypress'
])
const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null
  },
  label: {
    type: String,
    default: ''
  },
  measure: {
    type: String,
    default: ''
  },
  maxLength: {
    type: [String, Number],
    default: ''
  },
  maxValue: {
    type: Number,
    default: null
  },
  minValue: {
    type: Number,
    default: null
  },
  maskDecimalLimit: {
    type: Number,
    default: 2
  },
  maskIntegerLimit: {
    type: Number,
    default: 8
  },
  allowDecimal: {
    type: Boolean,
    default: true,
  },
  withMaxNotice: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  small: {
    type: Boolean,
    default: false,
  },
  big: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  lazy: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  rules: {
    type: Array as PropType<ValidationRule[]>,
    default: () => []
  },
  errorBlockStyle: {
    type: Boolean,
    default: true
  },
  hasInfoForBY: {
    type: Boolean,
    default: false
  }
})

// mixin
const validation = useValidateble()

async function checkError() {
  return hasError.value
}

const inputControl = ref<ComponentPublicInstance<typeof VzInput>>(null as any)
const inputValue = ref('')
const displayMaxNotice = ref(false)
const measureClass = computed((): { [x: string]: boolean } => {
  return {
    disabled: props.disabled,
    big: props.big
  }
})

const maxNoticeText = computed((): string => {
  return props.withMaxNotice && props.maxValue ? `Не более <span class="text-nowrap">${props.maxValue} ${props.measure}</span>` : ''
})

const hasError = computed(() => {
  return props.maxValue !== null && parseFloat(inputValue.value) > props.maxValue
})

    watch( () => props.modelValue, () => {
      if (props.modelValue !== inputValue.value) {
        inputValue.value = props.modelValue?.toString()
        inputControl.value.inputControl.value = inputValue.value
      }
    })

    watch(() => props.maxValue, () => {
      if (props.maxValue !== null && parseFloat(inputValue.value) > props.maxValue) {
        if (props.withMaxNotice) {
          displayMaxNotice.value = true
        } else {
          inputControl.value.input.select()
        }
      } else {
        displayMaxNotice.value = false
      }
    })

    onMounted( ():void => {
      if (props.modelValue !== null) {
        inputValue.value = props.modelValue?.toString()
        inputControl.value.inputControl.value = inputValue.value
        checkValue()
      }
    })

    const onBlur = (event: Event): void => {
      if (props.disabled) {
        return
      }
      checkMinValue()
      emit('blur', event)
    }

    const onChange = async (event: Event): Promise<void> => {
      checkMinValue()
      if (props.lazy) {
        emit('update:modelValue', inputValue.value)
        await nextTick()
      }
      emit('change', inputValue.value)
    }

    const onFocus = (event: Event): void => {
      inputControl.value.inputControl.select()
      emit('focus', event)
    }

    const onInput = (value: string): void => {
      if (props.disabled) {
        return
      }
      checkMaxValue()
    }


    const onClick = (event: MouseEvent): void => {
      emit('click', event)
    }

    const onDblClick = (event: MouseEvent): void => {
      emit('dblclick', event)
    }

    const onMousedown = (event: MouseEvent): void => {
      emit('mousedown', event)
    }

    const onMousemove = (event: MouseEvent): void => {
      emit('mousemove', event)
    }

    const onMouseout = (event: MouseEvent): void => {
      emit('mouseout',event)
    }

    const onMouseover = (event: MouseEvent): void => {
      emit('mouseover', event)
    }

    const onMouseup = (event: MouseEvent): void => {
      emit('mouseup', event)
    }


    const onMousewheel = (event: MouseEvent): void => {
      emit('mousewheel', event)
    }

    const onKeydown = (event: KeyboardEvent): void => {
      if (props.maskDecimalLimit === 0 && [',', '.'].includes(event.key)) {
        event.preventDefault()
        return
      }
      if ((event.code === 'Enter' || event.code === 'NumpadEnter') && event.target?.nodeName === 'INPUT') {
        let form = event.target.form
        if (form) {
          let index = Array.prototype.indexOf.call(form, event.target)
          form.elements[index + 1].focus()
          event.preventDefault()
        }
      }
      emit('keydown', event)
    }

    const onKeyup = (event: KeyboardEvent): void => {
      emit('keyup', event)
    }

    const onKeypress = (event:KeyboardEvent): void => {
      emit('keypress', event)
    }

    const mask = (rawValue: string) : string => {
      rawValue = rawValue.replace(/[юЮбБ,]/, '.')

      const mask = CreateNumberMask({
        allowDecimal: props.allowDecimal,
        decimalLimit: props.maskDecimalLimit,
        integerLimit: props.maskIntegerLimit,
        includeThousandsSeparator: false,
        prefix: ''
      })
      return mask(rawValue)
    }

    const checkMaxValue = (): void => {
      displayMaxNotice.value = false
      inputValue.value = VueMaskFilter(inputValue.value, mask)
      if (props.maxValue !== null && parseFloat(inputValue.value) > props.maxValue) {
        inputValue.value = VueMaskFilter(props.maxValue.toString(), mask)
        inputControl.value.inputControl.value = inputValue.value
        inputControl.value.inputControl.select()

        if (props.withMaxNotice) {
          displayMaxNotice.value = true
        } else {
          inputControl.value.input.select()
        }

        emitInput()
        return
      }
      inputControl.value.inputControl.value = inputValue.value
      emitInput()
    }

    const checkMinValue = (): void => {
      inputValue.value = VueMaskFilter(inputValue.value, mask)
      inputControl.value.inputControl.value = inputValue.value
      const toFLoat = inputValue.value === '' ? 0 : parseFloat(inputValue.value)
      if (props.clearable && toFLoat === 0) {
        inputValue.value = null
        emitInput()
        return
      }
      if (props.minValue !== null && toFLoat < props.minValue) {
        inputValue.value = props.minValue.toString() || '0' // VueMaskFilter(props.minValue.toString(), mask)
        emitInput()
        return
      }
      emitInput()
    }

    const checkValue = (): void => {
      checkMaxValue()
      checkMinValue()
    }

    function clear() {
      inputValue.value = null
      emit('update:modelValue', inputValue.value)
      emit('change', inputValue.value)
    }

    const emitInput = (): string|void => {
      if (!props.lazy) {
        emit('update:modelValue', inputValue.value)
      }
    }

defineExpose({
  checkError
})
</script>
